import { GridRowModes, GridToolbarContainer, GridActionsCellItem, useGridApiRef } from '@mui/x-data-grid-premium';
import { Field, FieldArray, useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';

import { dataGridPremiumColumns } from '../../utils/helper';

import {
  DataGridIdentifiers,
  FormErrorUI,
  AutoCompleteSelectUI,
  CheckboxUI,
  AddButtonUi,
  DatagridPremiumUi,
  InputUI,
} from '../Interface';

import useWindowSubtractedHeight from '../../hooks/useWindowSubtractedHeight';

function EditToolbar(props) {
  const { disabled, push, defaultCheckList } = props;
  const { values, validateField } = useFormikContext();

  const handleClick = async () => {
    const newData = {
      ...defaultCheckList,
      id: uuidv4(),
    };

    await push(newData);

    const fieldsToValidate = [
      `serviceCheckList.${values.serviceCheckList.length ?? 0}.name`,
      `serviceCheckList.${values.serviceCheckList.length ?? 0}.note`,
      `serviceCheckList.${values.serviceCheckList.length ?? 0}.attachRequired`,
      `serviceCheckList.${values.serviceCheckList.length ?? 0}.descriptionRequired`,
    ];

    for (const field of fieldsToValidate) {
      await validateField(field);
    }
  };

  return (
    <GridToolbarContainer>
      <AddButtonUi disabled={disabled} onClick={handleClick} />
    </GridToolbarContainer>
  );
}

const ServiceOperationCheckList = ({ disabled, defaultCheckList }) => {
  const apiRef = useGridApiRef();
  const { values, validateField, setFieldValue, errors } = useFormikContext();

  const gridHeight = useWindowSubtractedHeight({ otherContentHeight: 680, minHeight: 400 });

  const getValueIndex = (values, id) => {
    return values.serviceCheckList.findIndex((rc) => rc.id === id);
  };

  const columns = (setFieldValue, validateField, errors, remove) => {
    return dataGridPremiumColumns([
      {
        field: 'name',
        headerName: 'Megnevezés',
        type: 'string',
        editable: true,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`serviceCheckList.${valueIndex}.name`}
              type="text"
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.serviceCheckList?.[valueIndex]?.name} />}
              onChange={(e) => {
                setFieldValue(`serviceCheckList.${valueIndex}.name`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`serviceCheckList.${valueIndex}.name`)}
            />
          );
        },
      },
      {
        field: 'note',
        headerName: 'Megjegyzés',
        type: 'string',
        editable: true,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`serviceCheckList.${valueIndex}.note`}
              type="text"
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.serviceCheckList?.[valueIndex]?.note} />}
              onChange={(e) => {
                setFieldValue(`serviceCheckList.${valueIndex}.note`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`serviceCheckList.${valueIndex}.note`)}
            />
          );
        },
      },
      {
        field: 'attachRequired',
        type: 'boolean',
        headerName: 'Csatolt fájlt kötelező',
        editable: true,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          return (
            <Field
              name={`serviceCheckList.${valueIndex}.attachRequired`}
              component={CheckboxUI}
              helperText={<FormErrorUI message={errors?.serviceCheckList?.[valueIndex]?.attachRequired} />}
              checked={data.row?.attachRequired === true}
              variant="standard"
              onChange={(e) => {
                setFieldValue(`serviceCheckList.${valueIndex}.attachRequired`, e.target.checked ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.checked,
                });
              }}
              onBlur={async () => await validateField(`serviceCheckList.${valueIndex}.attachRequired`)}
            />
          );
        },
      },
      {
        field: 'descriptionRequired',
        type: 'boolean',
        headerName: 'Leírás kötelező',
        editable: true,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          return (
            <Field
              name={`serviceCheckList.${valueIndex}.descriptionRequired`}
              component={CheckboxUI}
              helperText={<FormErrorUI message={errors?.serviceCheckList?.[valueIndex]?.descriptionRequired} />}
              checked={data.row?.descriptionRequired === true}
              variant="standard"
              onChange={(e) => {
                setFieldValue(`serviceCheckList.${valueIndex}.descriptionRequired`, e.target.checked ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.checked,
                });
              }}
              onBlur={async () => await validateField(`serviceCheckList.${valueIndex}.descriptionRequired`)}
            />
          );
        },
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Szerk.',
        cellClassName: 'actions',
        sortable: false,
        getActions: ({ id }) => {
          return [
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete"
              onClick={() => {
                const valueIndex = getValueIndex(values, id);
                return remove(valueIndex);
              }}
              color="inherit"
              disabled={disabled}
            />,
          ];
        },
      },
    ]);
  };

  return (
    <div className="overflow-x-auto">
      <div className="align-middle">
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
          <FieldArray name="serviceCheckList">
            {({ remove, push }) => (
              <DatagridPremiumUi
                apiRef={apiRef}
                datagridIdentifier={DataGridIdentifiers.ServiceOperationCheckList}
                height={gridHeight}
                columns={columns(setFieldValue, validateField, errors, remove)}
                rows={values?.serviceCheckList ?? []}
                slots={{
                  toolbar: EditToolbar,
                }}
                headerFilters={false}
                slotProps={{
                  toolbar: {
                    disabled,
                    push,
                    defaultCheckList,
                  },
                }}
                onCellDoubleClick={(_params, event) => {
                  event.defaultMuiPrevented = true;
                }}
                editMode="row"
                getRowHeight={() => 'auto'}
                onCellClick={(params, event) => {
                  if (disabled) return;
                  const rowMode = apiRef.current.getRowMode(params.row.id);
                  if (rowMode === GridRowModes.View) apiRef.current.startRowEditMode({ id: params.row.id });
                }}
              />
            )}
          </FieldArray>
        </div>
      </div>
    </div>
  );
};

export default ServiceOperationCheckList;
